import type { TupleToUnion } from 'type-fest'

import { getErrorLogMessage } from './get-error-log-message.ts'

export type QuickFrameErrorOptions<ErrorCode extends string> = ErrorOptions & {
  code: ErrorCode
}

export abstract class QuickFrameError<ErrorCode extends string> extends Error {
  public code: QuickFrameErrorOptions<ErrorCode>['code']
  constructor(
    message: string,
    { code, ...options }: QuickFrameErrorOptions<ErrorCode>
  ) {
    super(message, options)
    this.name = this.constructor.name
    this.code = code
    Object.setPrototypeOf(this, new.target.prototype)
  }

  public get logMessage() {
    return getErrorLogMessage(this)
  }
}

// 400 - BAD_REQUEST
export class BadRequestError<
  ErrorCode extends string,
> extends QuickFrameError<ErrorCode> {}

export function isBadRequestError(
  error: unknown
): error is BadRequestError<string> {
  return error instanceof BadRequestError && typeof error.code === 'string'
}

export class RuleViolationError extends BadRequestError<'rule_violation'> {
  constructor(message: string) {
    super(message, { code: 'rule_violation' })
  }
}

// 401 - UNAUTHORIZED
const AuthenticationErrorCodes = [
  'missing_session',
  'missing_principal',
  'expired_session',
] as const
type AuthenticationErrorCode = TupleToUnion<typeof AuthenticationErrorCodes>

export class AuthenticationError extends QuickFrameError<AuthenticationErrorCode> {}

export function isAuthenticationError(
  error: unknown
): error is AuthenticationError {
  return error instanceof AuthenticationError
}

// 403 - FORBIDDEN
type AuthorizationErrorCode = 'forbidden'

export class AuthorizationError extends QuickFrameError<AuthorizationErrorCode> {
  constructor(
    message: string,
    options: Partial<QuickFrameErrorOptions<AuthorizationErrorCode>> = {}
  ) {
    super(message, { code: 'forbidden', ...options })
  }
}

export function isAuthorizationError(
  error: unknown
): error is AuthorizationError {
  return error instanceof AuthorizationError
}

// 404 - NOT_FOUND
export class NotFoundError<
  ErrorCode extends string,
> extends QuickFrameError<ErrorCode> {}

export function isNotFoundError(
  error: unknown
): error is NotFoundError<string> {
  return error instanceof NotFoundError
}

// 409 - CONFLICT
export class ConflictError<
  ErrorCode extends string,
> extends QuickFrameError<ErrorCode> {}

// 500 - INTERNAL_SERVER_ERROR
export class InternalServerError<
  ErrorCode extends string,
> extends QuickFrameError<ErrorCode> {}

// 501 - NOT_IMPLEMENTED
export class NotImplementedError<
  ErrorCode extends string,
> extends QuickFrameError<ErrorCode> {}

export class PrincipalNotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PrincipalNotFoundError'
  }
}

export function isPrincipalNotFoundError(
  error: unknown
): error is PrincipalNotFoundError {
  return error instanceof PrincipalNotFoundError
}

export class PrincipalInactiveError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PrincipalInactiveError'
  }
}

export function isPrincipalInactiveError(
  error: unknown
): error is PrincipalInactiveError {
  return error instanceof PrincipalInactiveError
}

export function isError(error: unknown): error is Error {
  return error instanceof Error
}

export function assertError(error: unknown): asserts error is Error {
  if (!isError(error)) {
    throw new Error(`Non-Error value thrown: ${JSON.stringify(error)}`)
  }
}
